/**
 * useGameColorMap · 全局游戏图例颜色统一 Hook
 *
 * 核心算法：槽位继承（Slot-based Color Allocation）
 * ================================================
 *
 * 需求背景：
 * - 最多同时选中 15 个游戏
 * - 每个游戏需要固定的图例颜色（跨图表一致）
 * - 当游戏 A 被移除、游戏 B 被添加时，B 应继承 A 的颜色（槽位复用）
 *   例如：初始选中 [A, B, C]，分别占用颜色槽 0/1/2
 *         移除 A → 槽位 0 释放
 *         添加 D  → D 继承槽位 0 的颜色（原本 A 的颜色）
 *
 * 算法逻辑：
 * 1. 维护 `slotMap: Map<gameId, slotIndex>` —— 记录每个游戏占用的槽位
 * 2. 维护 `occupiedSlots: Set<number>` —— 记录当前已被占用的槽位
 * 3. 每次 `game_ids` 变化时：
 *    a. 移除不再存在的 game_id → 释放其槽位
 *    b. 新 game_id → 分配最小可用槽位（优先填补空缺）
 *    c. 已有 game_id → 保持原槽位不变
 * 4. 颜色映射：`color = chartAllColors[slotIndex]`
 *
 * 透传方案：
 * - 通过 React Context（GameColorContext）向下传递 `gameColorMap`
 * - 图表组件通过 `useGameColorContext()` 获取映射
 * - 避免深层 props drilling
 */

import { useRef, useMemo } from 'react';
import { chartAllColors } from '@op/constants';

export type TGameColorMap = Record<string, string>;

/** 最大颜色槽位数 */
const MAX_SLOTS = chartAllColors.length; // 30

/**
 * 为给定的游戏 ID 列表计算颜色映射
 *
 * @param gameIds - 当前选中的游戏 ID 列表（有序）
 * @returns gameColorMap - `{ [gameId]: color }` 映射
 *
 * @example
 * ```ts
 * // 初始：A, B, C
 * const map1 = useGameColorMap(['A', 'B', 'C']);
 * // => { A: '#5086f3', B: '#69d19c', C: '#6e7794' }
 *
 * // 替换 A 为 D
 * const map2 = useGameColorMap(['D', 'B', 'C']);
 * // => { D: '#5086f3', B: '#69d19c', C: '#6e7794' }
 * // D 继承了 A 的颜色
 * ```
 */
export function useGameColorMap(gameIds: string[] = []): {
  gameColorMap: TGameColorMap;
  /** 直接获取某个游戏的图例颜色 */
  getGameColor: (gameId: string) => string;
} {
  // 持久化槽位映射（跨 render 保持）
  const slotMapRef = useRef<Map<string, number>>(new Map());

  const gameColorMap = useMemo(() => {
    const newGameIds = new Set(gameIds);
    const currentSlotMap = slotMapRef.current;
    const occupiedSlots = new Set<number>();

    // Step 1: 清理已移除的游戏，释放槽位；保留仍存在的游戏
    const keysToDelete: string[] = [];
    currentSlotMap.forEach((_slot, gameId) => {
      if (!newGameIds.has(gameId)) {
        keysToDelete.push(gameId);
      }
    });
    keysToDelete.forEach((key) => currentSlotMap.delete(key));

    // Step 2: 收集当前已被占用的槽位
    currentSlotMap.forEach((slot) => {
      occupiedSlots.add(slot);
    });

    // Step 3: 为新增游戏分配最小可用槽位
    gameIds.forEach((gameId) => {
      if (!currentSlotMap.has(gameId)) {
        // 查找最小可用槽位（优先填补空缺）
        let assignedSlot = -1;
        for (let i = 0; i < MAX_SLOTS; i++) {
          if (!occupiedSlots.has(i)) {
            assignedSlot = i;
            break;
          }
        }
        // 理论上不会超过 MAX_SLOTS（因为最多选 15 个游戏，30 个槽位足够）
        if (assignedSlot >= 0) {
          currentSlotMap.set(gameId, assignedSlot);
          occupiedSlots.add(assignedSlot);
        }
      }
    });

    // Step 4: 构建 gameId → color 映射
    const colorMap: TGameColorMap = {};
    currentSlotMap.forEach((slot, gameId) => {
      colorMap[gameId] = chartAllColors[slot] || chartAllColors[slot % MAX_SLOTS];
    });

    return colorMap;
  }, [gameIds]);

  const getGameColor = useMemo(
    () => (gameId: string): string => gameColorMap[gameId] || chartAllColors[0],
    [gameColorMap],
  );

  return { gameColorMap, getGameColor };
}
